import axios from "axios";
import type { AxiosRequestConfig, RawAxiosRequestHeaders } from "axios";

const VIDEO_APP_URL = process.env.VIDEO_APP_URL || "";
const VIDEO_APP_API_KEY = process.env.VIDEO_APP_API_KEY || "";

const getHeaders = (): RawAxiosRequestHeaders => {
  return {
    "Content-Type": "application/json",
    "x-api-key": VIDEO_APP_API_KEY,
  };
};

type MeetingStatus = "PENDING" | "COMPLETED" | "CANCELED";
type MeetingType = "SCHEDULED" | "ONE_TIME";

export type Participant = {
  name: string;
  id: number;
  meetingId: number;
  stUserId: number | null;
  email: string;
  phone: string | null;
  locale: string;
};

export type ParticipantCreatePayload = Pick<Participant, "name" | "email" | "phone" | "locale"> & {
  stUserId?: number;
};
export type ParticipantUpdatePayload = ParticipantCreatePayload;

type Feedback = {
  id: number;
  createdAt: Date;
  meetingId: number;
  rating: number;
  comment: string | null;
};

type MeetingMetaData = {
  translationDetails: {
    videoId: string;
    timestamp: string;
  };
};

export type MeetingLinks = {
  direct: {
    provider: string;
    patient: string;
  };
  embed: {
    provider: string;
    patient: string;
  };
};

export type Meeting = {
  id: number;
  uid: string;
  bookingId: number | null;
  providerId: number;
  userId: number | null;
  status: MeetingStatus;
  type: MeetingType;
  startTime: string;
  expires: string;
  createdAt: string;
  metadata: MeetingMetaData;
  feedback?: Feedback;
  links: MeetingLinks;
  participant: Participant;
};

export type MeetingCreatePayload = Pick<Meeting, "providerId" | "type" | "startTime"> & {
  bookingId?: number;
  participant: ParticipantCreatePayload;
};

export type MeetingUpdatePayload = Pick<Meeting, "startTime"> & {
  meetingId: Meeting["id"];
  participant: ParticipantUpdatePayload;
};

export type MeetingDeletePayload = {
  meetingId: number;
};

export type MeetingDeleteResponse = {
  message: string;
  meetingId: number;
  isTranslationTaskDeleted: boolean;
  videoId: string;
};

export type GetMeetingsParams = {
  providerId?: number;
  type?: MeetingType;
  page?: number;
  limit?: number;
  userId?: number;
  bookingId?: number;
  status?: "PENDING" | "COMPLETED" | "CANCELED";
  excludeExpired?: boolean;
};

export type Pagination = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type GetMeetingsResponse = {
  data: Meeting[];
  pagination: Pagination;
};

export function getMeetings(params: GetMeetingsParams): Promise<GetMeetingsResponse> {
  const config: AxiosRequestConfig = {
    method: "GET",
    url: VIDEO_APP_URL + "/api/meetings",
    headers: getHeaders(),
    params,
  };
  return new Promise<GetMeetingsResponse>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function createMeeting(payload: MeetingCreatePayload): Promise<{ meeting: Meeting }> {
  const config: AxiosRequestConfig = {
    method: "POST",
    url: VIDEO_APP_URL + "/api/meetings",
    headers: getHeaders(),
    data: payload,
  };
  return new Promise<{ meeting: Meeting }>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function updateMeeting(payload: MeetingUpdatePayload): Promise<Meeting> {
  const config: AxiosRequestConfig = {
    method: "PUT",
    url: VIDEO_APP_URL + "/api/meetings",
    headers: getHeaders(),
    data: payload,
  };
  return new Promise<Meeting>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

export function deleteMeeting(payload: MeetingDeletePayload): Promise<MeetingDeleteResponse> {
  const config: AxiosRequestConfig = {
    method: "DELETE",
    url: VIDEO_APP_URL + `/api/meetings?meetingId=${payload.meetingId}`,
    headers: getHeaders(),
  };
  return new Promise<MeetingDeleteResponse>((resolve, reject) => {
    axios(config)
      .then((response) => {
        resolve(response.data);
      })
      .catch((e) => {
        reject(e);
      });
  });
}

// TODO: Implement this function to avoid multiple API calls
export function deleteMeetingByBookingId(
  params: Pick<GetMeetingsParams, "providerId" | "bookingId">
): Promise<MeetingDeleteResponse> {
  return getMeetings(params)
    .then((resp) => {
      const targetMeeting = resp.data.find((m) => m.bookingId === params.bookingId);
      if (targetMeeting) {
        return deleteMeeting({ meetingId: targetMeeting.id });
      } else {
        return Promise.reject(new Error("Meeting not found"));
      }
    })
    .catch((e) => {
      return Promise.reject(e);
    });
}
